import { useFinancePageState } from "@/features/finance/hooks/use-finance-page-state"
import { FinanceCategoryScope } from "@/features/finance/model/finance-types"
import { useBranchCurrency } from "@/features/finance/hooks/use-branch-currency"

export interface FinanceReportScope {
  workspaceId: string | null
  /** Scoped branch id — the system (parish) branch in parish context, else the active branch. */
  branchId: string | null
  /** BRANCH vs PARISH, for the accounting/finance endpoints. */
  scope: FinanceCategoryScope
  context: "branch" | "parish"
  /** Active finance currency code. */
  currency: string
  canView: boolean
}

/**
 * One facade over the finance scope every report needs — workspace/branch/parish + currency — so each data
 * source reads scope from a single place (avoids the accounting-vs-engine parish/branch mismatch).
 */
export function useFinanceReportScope(): FinanceReportScope {
  const {
    workspaceId,
    activeContext,
    scopeBranch,
    parishBranch,
    canViewFinance,
  } = useFinancePageState()
  const currency = useBranchCurrency()

  const isParish = activeContext === "parish"
  const branchId = isParish ? parishBranch?.id ?? scopeBranch?.id ?? null : scopeBranch?.id ?? null

  return {
    workspaceId: workspaceId ?? null,
    branchId,
    scope: isParish ? FinanceCategoryScope.Parish : FinanceCategoryScope.Branch,
    context: activeContext,
    currency,
    // Resolved by the hook, which knows a mono workspace's single branch carries the parish authority.
    canView: canViewFinance,
  }
}
